import { useCallback, useEffect, useRef, useState } from 'react'
import type { Screen, NavTab, Profile } from './platform/types'
import { BottomNav } from './components/BottomNav'
import { HolidayGreeting } from './components/HolidayGreeting'
import { Capacitor } from '@capacitor/core'
import { useAuth } from './lib/auth'
import { useSubscription } from './lib/useSubscription'
import { consumeRoomNav, consumeScreenNav, subscribeRoomNav, isRoomLive } from './lib/navigationBus'
import { lookupWordTvRoomBySession } from './lib/api/wordTv'

// Platform screens
import { SplashScreen }      from './platform/hub/SplashScreen'
import { HubScreen }         from './platform/hub/HubScreen'
import { AuthScreen }        from './platform/auth/AuthScreen'
import { ResetPasswordScreen } from './platform/auth/ResetPasswordScreen'
import { PaywallScreen }     from './platform/subscriptions/PaywallScreen'
import { ProfileScreen }     from './platform/profile/ProfileScreen'
import { RulesLibraryScreen } from './platform/rules/RulesLibraryScreen'
import { PublicProfileScreen } from './platform/profile/PublicProfileScreen'
import { MyGameRoomsScreen } from './platform/hub/MyGameRoomsScreen'
import { EditProfileScreen } from './platform/profile/EditProfileScreen'
import { NotificationsScreen } from './platform/profile/NotificationsScreen'
import { PrivacyScreen }     from './platform/profile/PrivacyScreen'
import { LanguageScreen }    from './platform/profile/LanguageScreen'
import { FakeOutEndlessScreen }     from './games/word/FakeOutEndlessScreen'
import { CloseEnoughEndlessScreen } from './games/word/CloseEnoughEndlessScreen'
import { HelpScreen }        from './platform/profile/HelpScreen'
import { LivingRulebookScreen } from './platform/rules/LivingRulebookScreen'
import { PortablePartyListScreen } from './games/portable-party/screens/PortablePartyListScreen'
import { PortablePartyGameScreen } from './games/portable-party/screens/PortablePartyGameScreen'
import { SignInRequired }    from './components/SignInRequired'
import { WeeklyLandingScreen }  from './platform/weekly/WeeklyLandingScreen'
import { FakeOutScreen }        from './games/word/FakeOutScreen'
import { CloseEnoughScreen }    from './games/word/CloseEnoughScreen'
import { WordArchiveScreen }    from './games/word/WordArchiveScreen'
import { WeeklyResultsPopup }   from './platform/weekly/WeeklyResultsPopup'
import { ackWeeklyResults, fetchUnseenWeeklyResults, type UnseenWeeklyResult } from './lib/api/userFlags'
import { IETArchiveScreen }       from './games/ideatthat/screens/IETArchiveScreen'
import { ColorfulArchiveScreen }  from './games/colorful/screens/ColorfulArchiveScreen'

// I'd Eat That screens
import { LobbyScreen }            from './games/ideatthat/screens/LobbyScreen'
import { LiveRoomScreen }         from './games/ideatthat/screens/LiveRoomScreen'
import { IETOnlineMatchScreen }   from './games/ideatthat/screens/IETOnlineMatchScreen'
import { IdEatThatPassPlayApp }   from './games/ideatthat/passplay/IdEatThatPassPlayApp'
import { WeeklyHubScreen }        from './games/ideatthat/screens/WeeklyHubScreen'
import { SubmissionEditorScreen } from './games/ideatthat/screens/SubmissionEditorScreen'
import { VotingScreen }           from './games/ideatthat/screens/VotingScreen'
import { WinnersScreen }          from './games/ideatthat/screens/WinnersScreen'

// Colorful screens
import { ColorfulLobbyScreen }   from './games/colorful/screens/ColorfulLobbyScreen'
import { ColorfulPassPlayApp }   from './games/colorful/passplay/ColorfulPassPlayApp'
import { ColorfulRoomScreen }    from './games/colorful/screens/ColorfulRoomScreen'
import { ColorfulWeeklyHubScreen }     from './games/colorful/screens/ColorfulWeeklyHubScreen'
import { ColorfulWeeklyPlayScreen }    from './games/colorful/screens/ColorfulWeeklyPlayScreen'
import { ColorfulWeeklyWinnersScreen } from './games/colorful/screens/ColorfulWeeklyWinnersScreen'
import { ColorfulOnlineMatchScreen }   from './games/colorful/screens/ColorfulOnlineMatchScreen'

// Tokyo Metro screens
import { TokyoMetroRoomScreen }          from './games/tokyo-metro/screens/TokyoMetroRoomScreen'
import { TokyoMetroOnlineMatchScreen }   from './games/tokyo-metro/screens/TokyoMetroOnlineMatchScreen'

// Three Step Solution screens
import { ThreeStepLobbyScreen }            from './games/three-step/screens/ThreeStepLobbyScreen'
import { ThreeStepRoomScreen }             from './games/three-step/screens/ThreeStepRoomScreen'
import { ThreeStepOnlineMatchScreen }      from './games/three-step/screens/ThreeStepOnlineMatchScreen'
import { ThreeStepPassPlayApp }            from './games/three-step/passplay/ThreeStepPassPlayApp'
import { ThreeStepWeeklyHubScreen }        from './games/three-step/screens/ThreeStepWeeklyHubScreen'
import { ThreeStepWeeklyPlayScreen }       from './games/three-step/screens/ThreeStepWeeklyPlayScreen'
import { ThreeStepWeeklyVotingScreen }     from './games/three-step/screens/ThreeStepWeeklyVotingScreen'
import { ThreeStepWeeklyWinnersScreen }    from './games/three-step/screens/ThreeStepWeeklyWinnersScreen'
import { ThreeStepWeeklyArchiveScreen }    from './games/three-step/screens/ThreeStepWeeklyArchiveScreen'
import { useT } from './i18n'

/**
 * Top-level router.
 *
 * Auth screen is shown to unauth'd visitors. We still expose
 * instant-guest and instant-Apple-anon buttons so testing doesn't
 * require the email OTP round trip, but the user can actually see
 * the auth surface on every cold load.
 *
 * Flip AUTO_GUEST to true to silently auto-enter guest mode on boot
 * (skips the auth screen entirely — useful for rapid lobby testing).
 */
const AUTO_GUEST = false

// Screens that show the persistent bottom tab bar. The bar is rendered
// ONCE at the App level rather than inside each screen — per-screen
// copies unmounted with the outgoing screen on every tab switch, which
// made the bar blink for a frame on WKWebView while the incoming
// screen painted.
const NAV_SCREENS: ReadonlySet<Screen> = new Set<Screen>([
  'hub', 'weekly_landing', 'rules_library', 'profile',
  'iet_weekly_hub', 'colorful_weekly_hub', 'three_step_weekly_hub',
])

export default function App() {
  // Note: the public /privacy legal page is routed in main.tsx's
  // pickRoot() before App mounts, so the reviewer can reach it from a
  // cold load without tripping any of the auth/hook setup below.
  const { session, profile: profileRow, loading, isGuest, signOut, enterAsGuest, inPasswordRecovery } = useAuth()
  const t = useT()
  const subscription = useSubscription()

  // Testing mode: if nobody's signed in and they haven't opted into
  // guest mode explicitly, silently enter guest mode so they're not
  // stuck on the auth screen.
  useEffect(() => {
    if (loading) return
    if (!AUTO_GUEST) return
    if (!session && !isGuest) {
      enterAsGuest().catch(e => console.warn('[auth] auto-guest failed', e))
    }
  }, [loading, session, isGuest, enterAsGuest])

  const [bootDone, setBootDone] = useState(false)
  // Auto-resume a live room after an app reload/relaunch (e.g. the host's
  // phone froze and the app restarted). We persist {screen, roomId} while
  // in a game and restore it synchronously here, so the player lands back
  // in the room and the join/snapshot re-attaches them to their seat
  // instead of being stranded on the hub with "room no longer available".
  const savedRoom = (() => {
    try { return JSON.parse(localStorage.getItem('jdg_active_room') || 'null') as { screen: Screen; roomId: string } | null }
    catch { return null }
  })()

  // Stripe checkout round trip (web only): the success_url is the bare
  // app origin plus ?subscribed=, which used to strand the user on the
  // boot screen. The paywall transition below stashes the screen the
  // user came FROM; a successful checkout returns there, a cancelled
  // one returns to the paywall. The query param is stripped after use
  // so refresh/back can't replay it.
  const checkoutReturn = (() => {
    const flag = new URLSearchParams(window.location.search).get('subscribed')
    if (flag === null) return null
    window.history.replaceState(null, '', window.location.pathname)
    if (flag === '0') return { screen: 'paywall' as Screen, tab: 'hub' as NavTab }
    try {
      return JSON.parse(localStorage.getItem('jdg_paywall_return') || 'null') as
        { screen: Screen; tab: NavTab } | null
    } catch { return null }
  })()

  // A guest who scanned a word room's QR arrives at /join, which
  // resolves the code and hands it here rather than growing a second
  // copy of the game inside the join shell. Read once and strip, same
  // as the checkout round trip above. Parked in localStorage before the
  // URL is stripped: a scanning guest is usually signed OUT, lands on
  // the auth screen first, and would otherwise drop the code the
  // moment they sign in.
  const initialWordEntry = (() => {
    const KEY = 'jdg_word_join'
    const q = new URLSearchParams(window.location.search)
    const code = q.get('wordjoin')
    if (code) {
      const slug = q.get('game') === 'close-enough' ? 'close_enough_endless' : 'fake_out_endless'
      const stash = { code: code.toUpperCase(), screen: slug as Screen }
      try { localStorage.setItem(KEY, JSON.stringify(stash)) } catch { /* private mode */ }
      window.history.replaceState(null, '', window.location.pathname)
      return stash
    }
    try {
      return JSON.parse(localStorage.getItem(KEY) || 'null') as
        { code: string; screen: Screen } | null
    } catch { return null }
  })()

  // Held in STATE, not a per-render value. Clearing localStorage alone
  // would leave this truthy in memory for the rest of the mount, so
  // every later visit to the game would auto-join again instead of
  // showing the chooser. Also set by push/Jump-back-in re-entry.
  const [wordEntry, setWordEntry] = useState(initialWordEntry)
  const clearWordEntry = useCallback(() => {
    try { localStorage.removeItem('jdg_word_join') } catch { /* ignore */ }
    setWordEntry(null)
  }, [])

  // A guest arriving with a word-room code already chose to play: the
  // /join shell signed them in anonymously, promised the main app would
  // not bounce them, and handed the code here. Keep that promise - enter
  // guest mode silently instead of demanding a second "Continue as
  // guest" tap at the auth gate. Real accounts never pass through here
  // (their session is not anonymous), and a plain cold open without a
  // pending join keeps today's auth screen.
  const [guestHandoffFailed, setGuestHandoffFailed] = useState(false)
  useEffect(() => {
    if (loading || isGuest || !wordEntry) return
    const anon = Boolean((session?.user as { is_anonymous?: boolean } | undefined)?.is_anonymous)
    if (session && !anon) return
    enterAsGuest().catch(e => {
      console.warn('[auth] word-join guest handoff failed', e)
      setGuestHandoffFailed(true)
    })
  }, [loading, session, isGuest, wordEntry, enterAsGuest])

  const [screen, setScreen] = useState<Screen>(
    initialWordEntry?.screen ?? savedRoom?.screen ?? checkoutReturn?.screen ?? 'iet_weekly_hub')
  const [activeTab, setActiveTab] = useState<NavTab>(
    initialWordEntry ? 'hub' : (checkoutReturn?.tab ?? 'weekly'))



  // ── Browser-history integration (web only) ────────────────────────────
  // Screens are React state, invisible to the browser — so the Android
  // back button used to pop to the previous URL, which after a Stripe
  // checkout was the Stripe page itself. Each screen change now pushes
  // a history entry, and popstate walks app screens instead.
  const popNavigation = useRef(false)
  const screenRef = useRef<{ screen: Screen; tab: NavTab }>({ screen, tab: activeTab })
  useEffect(() => {
    screenRef.current = { screen, tab: activeTab }
    if (Capacitor.isNativePlatform()) return
    if (popNavigation.current) { popNavigation.current = false; return }
    window.history.pushState({ screen, tab: activeTab }, '')
  }, [screen, activeTab])
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return
    const onPop = (e: PopStateEvent) => {
      const st = e.state as { screen?: Screen; tab?: NavTab } | null
      if (st?.screen) {
        popNavigation.current = true
        setScreen(st.screen)
        if (st.tab) setActiveTab(st.tab)
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Remember where the paywall was opened from, for the checkout return.
  const prevScreenRef = useRef<Screen>(screen)
  useEffect(() => {
    if (screen === 'paywall' && prevScreenRef.current !== 'paywall') {
      try {
        localStorage.setItem('jdg_paywall_return',
          JSON.stringify({ screen: prevScreenRef.current, tab: activeTab }))
      } catch { /* storage full/blocked — checkout return falls back to boot screen */ }
    }
    prevScreenRef.current = screen
  }, [screen, activeTab])

  // Live-room flow state
  const [activeRoomId, setActiveRoomId] = useState<string | null>(
    savedRoom?.screen === 'iet_live' ? savedRoom.roomId : null,
  )

  // Weekly-event selections (event ids the user is acting on)
  const [editingEventId, setEditingEventId]   = useState<string | null>(null)
  const [votingEventId,  setVotingEventId]    = useState<string | null>(null)
  const [winnersEventId, setWinnersEventId]   = useState<string | null>(null)
  // Where "back" should land from any weekly sub-screen (editor,
  // voting, winners, colorful-weekly-winners). Set at nav time so
  // back respects the actual origin instead of always bouncing to
  // `iet_weekly_hub`.
  const [weeklyReturn, setWeeklyReturn] = useState<Screen>('weekly_landing')
  // Word-game archive target: which game, optionally a preselected
  // week (trophy taps), and where its back button should land.
  const [wordArchive, setWordArchive] = useState<{
    game: 'fake_out' | 'close_enough'; week: string | null; back: Screen
  }>({ game: 'fake_out', week: null, back: 'weekly_landing' })
  // Archive entry-point tracker — set when the user opens any of the
  // per-game archives so the archive's Back button returns to the
  // exact screen they came from (landing OR per-game hub) instead of
  // always bouncing to `weekly_landing`.
  const [archiveReturn, setArchiveReturn] = useState<Screen>('weekly_landing')

  // Colorful solo-vs-AI match state (lobby picks players, game owns state,
  // results screen reads it). All client-side.
  // Colorful hosted multiplayer room (server-backed, realtime).
  const [colorfulRoomId, setColorfulRoomId]   = useState<string | null>(
    savedRoom?.screen === 'colorful_room' ? savedRoom.roomId : null,
  )
  // Colorful weekly selections (event + entry id for this-week play, event id for past-week winners).
  const [colorfulWeeklyPlay, setColorfulWeeklyPlay]
    = useState<{ eventId: string; entryId: string } | null>(null)
  const [colorfulWeeklyWinnersId, setColorfulWeeklyWinnersId]
    = useState<string | null>(null)

  // Tokyo Metro matchmade room id (current active room).
  const [tokyoMetroRoomId, setTokyoMetroRoomId] = useState<string | null>(null)

  // Three Step weekly selections (event + entry id for this-week play,
  // event id for past-week voting / winners screens). Mirrors the
  // Colorful weekly state — same shape, different table.
  const [threeStepWeeklyPlay, setThreeStepWeeklyPlay]
    = useState<{ eventId: string; entryId: string } | null>(null)
  const [threeStepVotingId, setThreeStepVotingId]
    = useState<string | null>(null)
  const [threeStepWinnersId, setThreeStepWinnersId]
    = useState<string | null>(null)
  // Three Step live multiplayer room — set when entering an active
  // room via Quick Play / Party Play / Online Match. The room
  // screen owns all game state via realtime subscription + drives
  // AI seats client-side from the persona templates in
  // games/three-step/ai/personas.ts.
  const [threeStepRoomId, setThreeStepRoomId] = useState<string | null>(
    savedRoom?.screen === 'three_step_room' ? savedRoom.roomId : null,
  )

  // Persist the active live room so a reload can restore it (see savedRoom).
  useEffect(() => {
    const liveId =
      screen === 'iet_live'        ? activeRoomId :
      screen === 'colorful_room'   ? colorfulRoomId :
      screen === 'three_step_room' ? threeStepRoomId : null
    if (liveId) {
      localStorage.setItem('jdg_active_room', JSON.stringify({ screen, roomId: liveId }))
    } else if (screen !== 'iet_live' && screen !== 'colorful_room' && screen !== 'three_step_room') {
      localStorage.removeItem('jdg_active_room')
    }
  }, [screen, activeRoomId, colorfulRoomId, threeStepRoomId])

  // Selected Portable Party game number (1–50) for the detail screen.
  const [ppgGame, setPpgGame] = useState<number | null>(null)

  // The living rulebook the reader screen should show. Set at nav time
  // by the Rules tab; cleared on back.
  const [livingBook, setLivingBook] = useState<{
    slug: string; title: string; jsonUrl: string; locales: string[]; accent: string
  } | null>(null)

  // "Results are in" popup — checked once per app open for real
  // (non-anonymous) accounts. Rows are weeks the player participated
  // in whose results settled since they last looked; any dismissal
  // acknowledges everything shown so it never repeats.
  const [unseenResults, setUnseenResults] = useState<UnseenWeeklyResult[] | null>(null)
  const resultsCheckedFor = useRef<string | null>(null)
  useEffect(() => {
    const u = session?.user as { id?: string; is_anonymous?: boolean } | undefined
    if (!u?.id || u.is_anonymous || !profileRow) return
    if (resultsCheckedFor.current === u.id) return
    resultsCheckedFor.current = u.id
    fetchUnseenWeeklyResults()
      .then(rows => { if (rows.length > 0) setUnseenResults(rows) })
      .catch(() => { /* offline — check again next open */ })
  }, [session, profileRow])

  // The profile id to render in the public profile screen. Caller
  // sets this before navigating to 'public_profile'. Routes back to
  // wherever the caller came from (we stash that in `publicProfileReturn`).
  const [publicProfileId, setPublicProfileId] = useState<string | null>(null)
  const [publicProfileReturn, setPublicProfileReturn] = useState<Screen>('weekly_landing')

  /**
   * Return trail for the profile / winners web. A public profile links
   * to trophies, a trophy opens a winners screen (or a word-archive
   * chart), and those link back to profiles, so a single "return to"
   * slot cannot cover it: one lap of the cycle overwrites the way out
   * and the two screens end up pointing at each other forever. Every
   * hop INTO the web pushes a snapshot of the screen it left plus the
   * state that renders it, and back pops the exact screen it came
   * from. Entered from a hub, landing, or archive list the trail is
   * empty and back falls through to the old single-slot returns.
   */
  type NavSnapshot = {
    screen: Screen
    publicProfileId: typeof publicProfileId
    winnersEventId: typeof winnersEventId
    colorfulWeeklyWinnersId: typeof colorfulWeeklyWinnersId
    threeStepWinnersId: typeof threeStepWinnersId
    wordArchive: typeof wordArchive
  }
  const profileTrail = useRef<NavSnapshot[]>([])
  const pushProfileTrail = useCallback((overrides?: Partial<NavSnapshot>) => {
    profileTrail.current = [
      ...profileTrail.current.slice(-7),
      { screen, publicProfileId, winnersEventId, colorfulWeeklyWinnersId,
        threeStepWinnersId, wordArchive, ...overrides },
    ]
  }, [screen, publicProfileId, winnersEventId, colorfulWeeklyWinnersId,
      threeStepWinnersId, wordArchive])
  const popProfileTrail = useCallback((fallback: Screen) => {
    const prev = profileTrail.current[profileTrail.current.length - 1]
    if (!prev) { setScreen(fallback); return }
    profileTrail.current = profileTrail.current.slice(0, -1)
    setPublicProfileId(prev.publicProfileId)
    setWinnersEventId(prev.winnersEventId)
    setColorfulWeeklyWinnersId(prev.colorfulWeeklyWinnersId)
    setThreeStepWinnersId(prev.threeStepWinnersId)
    setWordArchive(prev.wordArchive)
    setScreen(prev.screen)
  }, [])

  /**
   * Helper passed to game screens so they can deep-link into a
   * public profile from any author tap. The trail remembers the
   * caller's screen so back returns there cleanly; returnTo is only
   * the fallback for an empty trail.
   */
  const openPublicProfile = useCallback((profileId: string, returnTo: Screen,
                                         overrides?: Partial<NavSnapshot>) => {
    if (!profileId) return
    pushProfileTrail(overrides)
    setPublicProfileId(profileId)
    setPublicProfileReturn(returnTo)
    setScreen('public_profile')
  }, [pushProfileTrail])

  // Show the branded splash briefly on first mount so boot isn't just a flash.
  useEffect(() => {
    const t = setTimeout(() => setBootDone(true), 1800)
    return () => clearTimeout(t)
  }, [])

  // When a fresh user signs in, land them on the games hub; guests start
  // on the weekly event since that's the only thing they can do.
  // EXCEPT on a cold start that restored a live room from
  // jdg_active_room — this effect used to stomp the restored screen
  // (and the persistence effect then deleted the saved key), which made
  // the whole auto-resume feature dead code. Skip the first redirect
  // when we resumed; later real sign-ins still land on the hub.
  const resumedFromSavedRoomRef = useRef<boolean>(Boolean(savedRoom))
  const wordEntryRoutedRef = useRef(false)
  useEffect(() => {
    if (session && profileRow) {
      if (resumedFromSavedRoomRef.current) {
        resumedFromSavedRoomRef.current = false
        return
      }
      // A guest who scanned a room QR signs in FROM the auth screen, so
      // this reset is the last thing to run and would drop them on the
      // hub holding a room code they never get to use. Send them to the
      // game instead; the screen claims the seat on mount.
      if (wordEntry && !wordEntryRoutedRef.current) {
        wordEntryRoutedRef.current = true
        setActiveTab('hub')
        setScreen(wordEntry.screen)
        return
      }
      setActiveTab('hub')
      setScreen('hub')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, profileRow?.id])

  // Push tap-through: when a room-scoped notification (e.g. "your turn in
  // online matches") is tapped, open that match — but only after confirming
  // it's still live. A finished / abandoned / gone room falls back to the hub
  // instead of stranding the user on a dead-room error. The intent is buffered
  // in navigationBus so a cold launch (tap fires before this subscribes) works.
  useEffect(() => {
    let cancelled = false
    const handle = async () => {
      // A push tap-through jumps hard, so any half-walked
      // profile/winners trail is stale from here on.
      profileTrail.current = []
      // Non-room pushes route straight to their screen (trophy -> profile,
      // weekly/vote pushes -> weekly landing). No liveness check needed.
      const screenTarget = consumeScreenNav()
      if (screenTarget === 'profile') { setActiveTab('profile'); setScreen('profile') }
      else if (screenTarget === 'weekly') { setActiveTab('weekly'); setScreen('weekly_landing') }

      const intent = consumeRoomNav()
      if (!intent) return
      const live = await isRoomLive(intent.game, intent.roomId)
      if (cancelled) return
      if (!live) { setActiveTab('hub'); setScreen('hub'); return }
      if (intent.game === 'iet')             { setActiveRoomId(intent.roomId);     setScreen('iet_live') }
      else if (intent.game === 'colorful')   { setColorfulRoomId(intent.roomId);   setScreen('colorful_room') }
      else if (intent.game === 'three_step') { setThreeStepRoomId(intent.roomId);  setScreen('three_step_room') }
      else if (intent.game === 'fake_out' || intent.game === 'close_enough') {
        // Re-entry after a drop (push tap or Jump back in). The intent
        // carries the SESSION id; resolve it back to the room code, and
        // word_endless_join hands back the seat you already hold rather
        // than burning a new one — resuming IS joining.
        const room = await lookupWordTvRoomBySession(intent.roomId)
        if (cancelled) return
        if (!room) { setActiveTab('hub'); setScreen('hub'); return }
        const target: Screen = room.game === 'close_enough' ? 'close_enough_endless' : 'fake_out_endless'
        setWordEntry({ code: room.code, screen: target })
        setActiveTab('hub')
        setScreen(target)
      }
    }
    const unsub = subscribeRoomNav(() => { void handle() })
    void handle() // flush any intent buffered before we subscribed (cold launch)
    return () => { cancelled = true; unsub() }
  }, [])

  const navigate = useCallback((s: Screen) => setScreen(s), [])

  const handleNavTab = useCallback((tab: NavTab) => {
    profileTrail.current = [] // a tab tap abandons any profile/winners web
    setActiveTab(tab)
    if (tab === 'hub')     setScreen('hub')
    if (tab === 'weekly')  setScreen('weekly_landing')
    if (tab === 'rules')   setScreen('rules_library')
    if (tab === 'profile') setScreen('profile')
  }, [])

  // ── Gate: boot splash ───────────────────────────────────────────────────
  if (!bootDone || loading) {
    return <SplashScreen onComplete={() => setBootDone(true)} />
  }

  // ── Gate: password recovery (Supabase opened a temporary session
  // off the email link). Force the user through the reset flow before
  // anything else, regardless of any cached state. ──────────────────────
  if (inPasswordRecovery) {
    return <ResetPasswordScreen />
  }

  // ── Gate: only force AuthScreen if AUTO_GUEST is off ────────────────────
  // A Supabase anonymous session does NOT count as "signed in" — the
  // user still sees the auth screen until they either pick a real
  // account (email / Apple) or tap "Continue as guest" explicitly.
  const isAnon = Boolean((session?.user as { is_anonymous?: boolean } | undefined)?.is_anonymous)
  const signedIn = Boolean(session && profileRow && !isAnon)
  if (!AUTO_GUEST && !signedIn && !isGuest) {
    // Word-room hand-off: guest mode is being entered silently by the
    // effect above. Hold the splash for that beat instead of flashing
    // the auth screen the /join shell promised to skip. If the silent
    // entry failed (offline, Supabase down), fall through to the auth
    // screen so the manual guest button still works.
    if (wordEntry && !guestHandoffFailed) {
      return <SplashScreen onComplete={() => setBootDone(true)} />
    }
    return <AuthScreen />
  }
  // Waiting for auto-guest to land — render the splash one more beat
  // so we don't flash an empty hub before the session is ready.
  if (AUTO_GUEST && !session && !isGuest) {
    return <SplashScreen onComplete={() => setBootDone(true)} />
  }

  // Synthesize a profile for guests so downstream screens don't crash on
  // null. Real users use their real row.
  const profile: Profile = signedIn && profileRow ? {
    id: profileRow.id,
    username: profileRow.username,
    display_name: profileRow.display_name,
    avatar_url: profileRow.avatar_url,
    subscription_status: subscription.isActive ? 'active' : 'inactive',
    subscription_expiry: subscription.currentPeriodEnd,
  } : {
    id: 'guest',
    username: 'guest',
    display_name: 'Guest',
    avatar_url: null,
    subscription_status: 'inactive',
    subscription_expiry: null,
  }

  // Trophies are now fetched inside ProfileScreen via list_my_trophies.

  // ── Guest gates (testing mode: only the weekly-event submit is gated) ───
  // During testing, guests can play every game and browse every screen.
  // Only the two places that require a real account stay locked:
  //   - submitting to the weekly event (writes to the sponsored table)
  //   - the profile screen (there's no profile for anon sessions yet)
  // Flip AUTO_GUEST off to bring back the full pre-launch gating.
  if (!signedIn) {
    if (screen === 'profile') {
      return (
        <SignInRequired
          action="view your profile"
          detail={t('You need an account to save progress and unlock trophies.')}
          onBack={() => handleNavTab('hub')}
        />
      )
    }
    if (screen === 'iet_weekly_editor') {
      return (
        <SignInRequired
          action="submit a dish"
          detail={t('Guests can read the weekly event, but voting and submitting a story require an account.')}
          onBack={() => handleNavTab('weekly')}
        />
      )
    }
  }

  // ── Screen router ───────────────────────────────────────────────────────
  // The switch runs inside an IIFE so the persistent chrome (BottomNav)
  // can render OUTSIDE it — the bar survives screen unmounts.
  const view = (() => { switch (screen) {

    case 'paywall':
      return <PaywallScreen onDismiss={() => navigate('hub')} />


    case 'hub':
      return (
        <HubScreen
          profile={profile}
          onNavigate={handleNavTab}
          onPlayIdEatThat={() => navigate('iet_lobby')}
          onPlayColorful={() => navigate('colorful_lobby')}
          onPlayTokyoMetro={() => {
            // Tokyo Metro is online-matchmaking-only — no free local
            // mode — so the whole game is paid.
            if (!subscription.loading && !subscription.isActive) navigate('paywall')
            else navigate('tokyo_metro_online_match')
          }}
          onPlayThreeStep={() => navigate('three_step_lobby')}
          onPlayPortableParty={() => {
            // PPG is All-Access only — the whole catalog (rules AND
            // notes) sits behind the sub, same as Tokyo Metro.
            if (!subscription.loading && !subscription.isActive) navigate('paywall')
            else navigate('portable_party_list')
          }}
          onPlayFakeOutEndless={() => {
            // Open to everyone: joining a room by code is the free path
            // (same rule as the other games). Solo, pass play, and
            // hosting gate themselves inside the lobby.
            navigate('fake_out_endless')
          }}
          onPlayCloseEnoughEndless={() => {
            navigate('close_enough_endless')
          }}
          onWeeklyEvent={(game) => {
            setActiveTab('weekly')
            navigate(game === 'colorful' ? 'colorful_weekly_hub'
              : game === 'three_step' ? 'three_step_weekly_hub'
              : 'iet_weekly_hub')
          }}
          onOpenPaywall={() => navigate('paywall')}
          onOpenMyRooms={() => navigate('my_game_rooms')}
        />
      )

    // Portable Party Games — All-Access only. The hub handler already
    // gates entry; these render-time gates cover every other path onto
    // the screens (stale state, future deep links), same fail-open-
    // while-loading rule as useGatedAction.
    case 'portable_party_list':
      if (!subscription.loading && !subscription.isActive) { navigate('paywall'); return null }
      return (
        <PortablePartyListScreen
          onBack={() => navigate('hub')}
          onOpenGame={(n) => { setPpgGame(n); navigate('portable_party_game') }}
        />
      )

    case 'portable_party_game':
      if (!subscription.loading && !subscription.isActive) { navigate('paywall'); return null }
      return (
        <PortablePartyGameScreen
          gameNumber={ppgGame ?? 6}
          onBack={() => navigate('portable_party_list')}
        />
      )

    case 'rules_library':
      return (
        <RulesLibraryScreen
          profile={profile}
          onProfileTap={() => { setActiveTab('profile'); setScreen('profile') }}
          onOpenLivingBook={(book) => { setLivingBook(book); navigate('living_rulebook') }}
        />
      )

    case 'living_rulebook':
      if (!livingBook) { navigate('rules_library'); return null }
      return (
        <LivingRulebookScreen
          slug={livingBook.slug}
          title={livingBook.title}
          jsonUrl={livingBook.jsonUrl}
          locales={livingBook.locales}
          accent={livingBook.accent}
          onBack={() => navigate('rules_library')}
        />
      )

    case 'profile':
      return (
        <ProfileScreen
          profile={profile}
          onEditProfile={() => navigate('edit_profile')}
          onOpenNotifications={() => navigate('notifications')}
          onOpenLanguage={() => navigate('language')}
          onOpenPrivacy={() => navigate('privacy')}
          onOpenHelp={() => navigate('help')}
          onSignOut={async () => { await signOut(); }}
          onOpenTrophy={(t) => {
            // Open the week the trophy was won in; the trail brings
            // the reader back to this shelf.
            pushProfileTrail()
            if (t.game_id === 'fake_out' || t.game_id === 'close_enough') {
              setWordArchive({ game: t.game_id as 'fake_out' | 'close_enough',
                               week: t.week_start, back: 'profile' })
              navigate('word_archive')
            } else if (t.game_id === 'colorful') {
              setColorfulWeeklyWinnersId(t.event_id)
              navigate('colorful_weekly_winners')
            } else if (t.game_id === 'three_step') {
              setThreeStepWinnersId(t.event_id)
              navigate('three_step_weekly_winners')
            } else {
              setWinnersEventId(t.event_id)
              navigate('iet_weekly_winners')
            }
          }}
        />
      )

    case 'edit_profile':
      return (
        <EditProfileScreen
          profile={profile}
          onBack={() => navigate('profile')}
        />
      )

    case 'my_game_rooms':
      return <MyGameRoomsScreen onBack={() => navigate('hub')} />

    case 'public_profile':
      if (!publicProfileId) { navigate(publicProfileReturn); return null }
      return (
        <PublicProfileScreen
          profileId={publicProfileId}
          onBack={() => popProfileTrail(publicProfileReturn)}
          onOpenTrophy={(t) => {
            // Same jump as the owner's shelf. The pushed snapshot keeps
            // this person's id, so back re-renders the same shelf, and
            // the earlier snapshots unwind to wherever it was opened
            // from, however many profile-to-winners laps deep.
            pushProfileTrail()
            if (t.game_id === 'fake_out' || t.game_id === 'close_enough') {
              setWordArchive({ game: t.game_id as 'fake_out' | 'close_enough',
                               week: t.week_start, back: 'public_profile' })
              navigate('word_archive')
            } else if (t.game_id === 'colorful') {
              setColorfulWeeklyWinnersId(t.event_id)
              navigate('colorful_weekly_winners')
            } else if (t.game_id === 'three_step') {
              setThreeStepWinnersId(t.event_id)
              navigate('three_step_weekly_winners')
            } else {
              setWinnersEventId(t.event_id)
              navigate('iet_weekly_winners')
            }
          }}
        />
      )

    case 'notifications':
      return <NotificationsScreen onBack={() => navigate('profile')} />

    // Endless word games. The SCREENS are open to everyone — joining a
    // room by code is the free path, and a screen-level gate here is
    // exactly what bounced invited guests to the paywall last time
    // (1f7fe13). Solo, pass play, and hosting gate themselves inside
    // the lobby via useGatedAction; the server gates session creation.
    case 'fake_out_endless':
      return (
        <FakeOutEndlessScreen
          onBack={() => navigate('hub')}
          onPlayWeekly={() => { setActiveTab('weekly'); navigate('fake_out_daily') }}
          onOpenPaywall={() => navigate('paywall')}
          resumeCode={wordEntry?.screen === 'fake_out_endless' ? wordEntry.code : undefined}
          onResumeConsumed={clearWordEntry}
        />
      )

    case 'close_enough_endless':
      return (
        <CloseEnoughEndlessScreen
          onBack={() => navigate('hub')}
          onPlayWeekly={() => { setActiveTab('weekly'); navigate('close_enough_daily') }}
          onOpenPaywall={() => navigate('paywall')}
          resumeCode={wordEntry?.screen === 'close_enough_endless' ? wordEntry.code : undefined}
          onResumeConsumed={clearWordEntry}
        />
      )

    case 'language':
      return <LanguageScreen onBack={() => navigate('profile')} />

    case 'privacy':
      return <PrivacyScreen profile={profile} onBack={() => navigate('profile')} />

    case 'help':
      return <HelpScreen onBack={() => navigate('profile')} />

    // ─── I'd Eat That — live multiplayer ─────────────────────────────────

    case 'iet_lobby':
      return (
        <LobbyScreen
          onBack={() => navigate('hub')}
          onEnteredRoom={(roomId) => {
            setActiveRoomId(roomId)
            navigate('iet_live')
          }}
          onOpenWeekly={() => { setActiveTab('weekly'); navigate('iet_weekly_hub') }}
          onOnlineMatch={() => navigate('iet_online_match')}
          onPassPlay={() => navigate('iet_passplay')}
          onOpenPaywall={() => navigate('paywall')}
        />
      )

    case 'iet_passplay':
      return <IdEatThatPassPlayApp onExit={() => navigate('iet_lobby')} />

    case 'iet_online_match':
      return (
        <IETOnlineMatchScreen
          onBack={() => navigate('iet_lobby')}
          onMatched={(roomId) => {
            setActiveRoomId(roomId)
            navigate('iet_live')
          }}
        />
      )

    case 'iet_live':
      if (!activeRoomId) { navigate('iet_lobby'); return null }
      return (
        <LiveRoomScreen
          roomId={activeRoomId}
          onExit={() => {
            setActiveRoomId(null)
            setActiveTab('hub')
            navigate('hub')
          }}
        />
      )

    // ─── I'd Eat That — weekly event ─────────────────────────────────────

    case 'weekly_landing':
      return (
        <WeeklyLandingScreen
          onNavigate={handleNavTab}
          onIETWeekly={() => navigate('iet_weekly_hub')}
          onColorfulWeekly={() => navigate('colorful_weekly_hub')}
          onThreeStepWeekly={() => navigate('three_step_weekly_hub')}
          onFakeOut={() => navigate('fake_out_daily')}
          onCloseEnough={() => navigate('close_enough_daily')}
          onFakeOutArchive={() => {
            setWordArchive({ game: 'fake_out', week: null, back: 'weekly_landing' })
            navigate('word_archive')
          }}
          onCloseEnoughArchive={() => {
            setWordArchive({ game: 'close_enough', week: null, back: 'weekly_landing' })
            navigate('word_archive')
          }}
          onVoteIET={(eventId) => {
            setVotingEventId(eventId)
            setWeeklyReturn('weekly_landing')
            navigate('iet_weekly_voting')
          }}
          onVoteThreeStep={(eventId) => {
            setThreeStepVotingId(eventId)
            setWeeklyReturn('weekly_landing')
            navigate('three_step_weekly_voting')
          }}
          onOpenIETArchive={() => { setArchiveReturn('weekly_landing'); navigate('iet_archive') }}
          onOpenColorfulArchive={() => { setArchiveReturn('weekly_landing'); navigate('colorful_archive') }}
          onOpenThreeStepArchive={() => { setArchiveReturn('weekly_landing'); navigate('three_step_archive') }}
        />
      )

    case 'fake_out_daily':
      return <FakeOutScreen onBack={() => navigate('weekly_landing')} />

    case 'close_enough_daily':
      return <CloseEnoughScreen onBack={() => navigate('weekly_landing')} />

    case 'word_archive':
      return (
        <WordArchiveScreen
          game={wordArchive.game}
          initialWeek={wordArchive.week}
          onBack={() => popProfileTrail(wordArchive.back)}
          onOpenProfile={(id, week) => {
            // Pin the viewed week so back lands on the same chart, not
            // the week list this screen was opened with.
            const wa = { ...wordArchive, week }
            setWordArchive(wa)
            openPublicProfile(id, 'word_archive', { wordArchive: wa })
          }}
        />
      )

    case 'iet_archive':
      return (
        <IETArchiveScreen
          onBack={() => navigate(archiveReturn)}
          onViewWeek={(eventId) => {
            setWinnersEventId(eventId)
            setWeeklyReturn('iet_archive')
            navigate('iet_weekly_winners')
          }}
        />
      )

    case 'colorful_archive':
      return (
        <ColorfulArchiveScreen
          onBack={() => navigate(archiveReturn)}
          onViewWeek={(eventId) => {
            setColorfulWeeklyWinnersId(eventId)
            setWeeklyReturn('colorful_archive')
            navigate('colorful_weekly_winners')
          }}
        />
      )

    case 'iet_weekly_hub':
      return (
        <WeeklyHubScreen
          onWriteStory={(eventId) => {
            // Submitting to a weekly event is paid (writes to the
            // sponsored table). Reading + voting is free.
            if (subscription.loading) return
            if (!subscription.isActive) {
              navigate('paywall'); return
            }
            setEditingEventId(eventId)
            setWeeklyReturn('iet_weekly_hub')
            navigate('iet_weekly_editor')
          }}
          onVoteOnWeek={(eventId) => {
            setVotingEventId(eventId)
            setWeeklyReturn('iet_weekly_hub')
            navigate('iet_weekly_voting')
          }}
          onBackToWeekly={() => navigate('weekly_landing')}
          onOpenArchive={() => { setArchiveReturn('iet_weekly_hub'); navigate('iet_archive') }}
        />
      )

    case 'iet_weekly_editor':
      if (!editingEventId) { navigate(weeklyReturn); return null }
      return (
        <SubmissionEditorScreen
          eventId={editingEventId}
          onBack={() => navigate(weeklyReturn)}
          onSubmitted={() => navigate(weeklyReturn)}
        />
      )

    case 'iet_weekly_voting':
      if (!votingEventId) { navigate(weeklyReturn); return null }
      return (
        <VotingScreen
          eventId={votingEventId}
          onBack={() => navigate(weeklyReturn)}
          onOpenProfile={(id) => openPublicProfile(id, 'iet_weekly_voting')}
        />
      )

    case 'iet_weekly_winners':
      if (!winnersEventId) { navigate(weeklyReturn); return null }
      return (
        <WinnersScreen
          eventId={winnersEventId}
          onBack={() => popProfileTrail(weeklyReturn)}
          onPlayAgain={() => signedIn ? navigate('iet_lobby') : handleNavTab('weekly')}
          onOpenProfile={(id) => openPublicProfile(id, 'iet_weekly_winners')}
        />
      )

    // ─── Colorful — hot-seat ─────────────────────────────────────────────

    case 'colorful_lobby':
      return (
        <ColorfulLobbyScreen
          onBack={() => navigate('hub')}
          onOpenWeekly={() => { setActiveTab('weekly'); navigate('colorful_weekly_hub') }}
          onHosted={(roomId) => {
            setColorfulRoomId(roomId)
            navigate('colorful_room')
          }}
          onOnlineMatch={() => navigate('colorful_online_match')}
          onPassPlay={() => navigate('colorful_passplay')}
          onOpenPaywall={() => navigate('paywall')}
        />
      )

    case 'colorful_passplay':
      return <ColorfulPassPlayApp onExit={() => navigate('colorful_lobby')} />

    case 'colorful_online_match':
      return (
        <ColorfulOnlineMatchScreen
          onBack={() => navigate('colorful_lobby')}
          onMatched={(roomId) => {
            setColorfulRoomId(roomId)
            navigate('colorful_room')
          }}
        />
      )

    // ─── Tokyo Metro ─────────────────────────────────────────────────────

    case 'tokyo_metro_online_match':
      return (
        <TokyoMetroOnlineMatchScreen
          onBack={() => navigate('hub')}
          onMatched={(roomId) => {
            setTokyoMetroRoomId(roomId)
            navigate('tokyo_metro_room')
          }}
        />
      )

    case 'tokyo_metro_room':
      if (!tokyoMetroRoomId) { navigate('hub'); return null }
      return (
        <TokyoMetroRoomScreen
          roomId={tokyoMetroRoomId}
          onExit={() => { setTokyoMetroRoomId(null); navigate('hub') }}
        />
      )

    case 'colorful_room':
      if (!colorfulRoomId) { navigate('colorful_lobby'); return null }
      return (
        <ColorfulRoomScreen
          roomId={colorfulRoomId}
          onExit={() => { setColorfulRoomId(null); navigate('hub') }}
        />
      )

    // ─── Colorful — weekly async event ────────────────────────────────────

    case 'colorful_weekly_hub':
      return (
        <ColorfulWeeklyHubScreen
          onBack={() => navigate('weekly_landing')}
          onPlayThisWeek={(eventId, entryId) => {
            // Submitting to a weekly event is paid (writes to the
            // sponsored table). Reading + voting is free.
            if (subscription.loading) return
            if (!subscription.isActive) {
              navigate('paywall'); return
            }
            setColorfulWeeklyPlay({ eventId, entryId })
            setWeeklyReturn('colorful_weekly_hub')
            navigate('colorful_weekly_play')
          }}
          onOpenArchive={() => { setArchiveReturn('colorful_weekly_hub'); navigate('colorful_archive') }}
        />
      )

    case 'colorful_weekly_play':
      if (!colorfulWeeklyPlay) { navigate(weeklyReturn); return null }
      return (
        <ColorfulWeeklyPlayScreen
          eventId={colorfulWeeklyPlay.eventId}
          entryId={colorfulWeeklyPlay.entryId}
          onBack={() => navigate(weeklyReturn)}
        />
      )

    case 'colorful_weekly_winners':
      if (!colorfulWeeklyWinnersId) { navigate(weeklyReturn); return null }
      return (
        <ColorfulWeeklyWinnersScreen
          eventId={colorfulWeeklyWinnersId}
          onBack={() => popProfileTrail(weeklyReturn)}
          onOpenProfile={(id) => openPublicProfile(id, 'colorful_weekly_winners')}
        />
      )

    // ─── Three Step Solution ──────────────────────────────────────────────

    case 'three_step_lobby':
      return (
        <ThreeStepLobbyScreen
          onBack={() => navigate('hub')}
          onOpenWeekly={() => { setActiveTab('weekly'); navigate('three_step_weekly_hub') }}
          onEnteredRoom={(roomId) => {
            setThreeStepRoomId(roomId)
            navigate('three_step_room')
          }}
          onOnlineMatch={() => navigate('three_step_online_match')}
          onPassPlay={() => navigate('three_step_passplay')}
          onOpenPaywall={() => navigate('paywall')}
        />
      )

    case 'three_step_passplay':
      return <ThreeStepPassPlayApp onExit={() => navigate('three_step_lobby')} />

    case 'three_step_room':
      if (!threeStepRoomId) { navigate('three_step_lobby'); return null }
      return (
        <ThreeStepRoomScreen
          roomId={threeStepRoomId}
          onExit={() => {
            setThreeStepRoomId(null)
            setActiveTab('hub')
            navigate('hub')
          }}
        />
      )

    case 'three_step_online_match':
      return (
        <ThreeStepOnlineMatchScreen
          onBack={() => navigate('three_step_lobby')}
          onMatched={(roomId) => {
            setThreeStepRoomId(roomId)
            navigate('three_step_room')
          }}
        />
      )

    case 'three_step_weekly_hub':
      return (
        <ThreeStepWeeklyHubScreen
          onBack={() => navigate('weekly_landing')}
          onPlayThisWeek={(eventId, entryId) => {
            // Submitting to a weekly event is paid (writes to the
            // sponsored table). Reading + voting is free.
            if (subscription.loading) return
            if (!subscription.isActive) {
              navigate('paywall'); return
            }
            setThreeStepWeeklyPlay({ eventId, entryId })
            setWeeklyReturn('three_step_weekly_hub')
            navigate('three_step_weekly_play')
          }}
          onVoteOnWeek={(eventId) => {
            setThreeStepVotingId(eventId)
            setWeeklyReturn('three_step_weekly_hub')
            navigate('three_step_weekly_voting')
          }}
          onOpenArchive={() => { setArchiveReturn('three_step_weekly_hub'); navigate('three_step_archive') }}
        />
      )

    case 'three_step_weekly_play':
      if (!threeStepWeeklyPlay) { navigate(weeklyReturn); return null }
      return (
        <ThreeStepWeeklyPlayScreen
          eventId={threeStepWeeklyPlay.eventId}
          entryId={threeStepWeeklyPlay.entryId}
          onBack={() => navigate(weeklyReturn)}
        />
      )

    case 'three_step_weekly_voting':
      if (!threeStepVotingId) { navigate(weeklyReturn); return null }
      return (
        <ThreeStepWeeklyVotingScreen
          eventId={threeStepVotingId}
          onBack={() => navigate(weeklyReturn)}
          onOpenProfile={(id) => openPublicProfile(id, 'three_step_weekly_voting')}
        />
      )

    case 'three_step_weekly_winners':
      if (!threeStepWinnersId) { navigate(weeklyReturn); return null }
      return (
        <ThreeStepWeeklyWinnersScreen
          eventId={threeStepWinnersId}
          onBack={() => popProfileTrail(weeklyReturn)}
          onOpenProfile={(id) => openPublicProfile(id, 'three_step_weekly_winners')}
        />
      )

    case 'three_step_archive':
      return (
        <ThreeStepWeeklyArchiveScreen
          onBack={() => navigate(archiveReturn)}
          onViewWeek={(eventId) => {
            setThreeStepWinnersId(eventId)
            setWeeklyReturn('three_step_archive')
            navigate('three_step_weekly_winners')
          }}
        />
      )

    default:
      return (
        <HubScreen
          profile={profile}
          onNavigate={handleNavTab}
          onPlayIdEatThat={() => navigate('iet_lobby')}
          onPlayColorful={() => navigate('colorful_lobby')}
          onPlayTokyoMetro={() => {
            // Tokyo Metro is online-matchmaking-only — no free local
            // mode — so the whole game is paid.
            if (!subscription.loading && !subscription.isActive) navigate('paywall')
            else navigate('tokyo_metro_online_match')
          }}
          onPlayThreeStep={() => navigate('three_step_lobby')}
          onPlayPortableParty={() => {
            // PPG is All-Access only — the whole catalog (rules AND
            // notes) sits behind the sub, same as Tokyo Metro.
            if (!subscription.loading && !subscription.isActive) navigate('paywall')
            else navigate('portable_party_list')
          }}
          onPlayFakeOutEndless={() => {
            // Open to everyone: joining a room by code is the free path
            // (same rule as the other games). Solo, pass play, and
            // hosting gate themselves inside the lobby.
            navigate('fake_out_endless')
          }}
          onPlayCloseEnoughEndless={() => {
            navigate('close_enough_endless')
          }}
          onWeeklyEvent={(game) => {
            setActiveTab('weekly')
            navigate(game === 'colorful' ? 'colorful_weekly_hub'
              : game === 'three_step' ? 'three_step_weekly_hub'
              : 'iet_weekly_hub')
          }}
          onOpenPaywall={() => navigate('paywall')}
          onOpenMyRooms={() => navigate('my_game_rooms')}
        />
      )
  } })()

  return (
    <>
      {view}
      <HolidayGreeting />
      {NAV_SCREENS.has(screen) && (
        <BottomNav active={activeTab} onNavigate={handleNavTab} />
      )}
      {unseenResults && (
        <WeeklyResultsPopup
          results={unseenResults}
          onSeeResults={(r) => {
            ackWeeklyResults(unseenResults).catch(() => { /* re-shows next open */ })
            setUnseenResults(null)
            setActiveTab('weekly')
            setWeeklyReturn('weekly_landing')
            if (r.game === 'colorful') {
              setColorfulWeeklyWinnersId(r.event_id)
              navigate('colorful_weekly_winners')
            } else if (r.game === 'three_step') {
              setThreeStepWinnersId(r.event_id)
              navigate('three_step_weekly_winners')
            } else if (r.game === 'fake_out' || r.game === 'close_enough') {
              // Word games have no winners screen of their own: the
              // archive IS the results view, opened on that week.
              setWordArchive({ game: r.game, week: r.week_start, back: 'weekly_landing' })
              navigate('word_archive')
            } else {
              setWinnersEventId(r.event_id)
              navigate('iet_weekly_winners')
            }
          }}
          onDismiss={() => {
            ackWeeklyResults(unseenResults).catch(() => { /* re-shows next open */ })
            setUnseenResults(null)
          }}
        />
      )}
    </>
  )
}
